"use client";

import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandItem,
  CommandEmpty,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Command as CommandPrimitive } from "cmdk";
import { X as RemoveIcon, Check } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import React, {
  type KeyboardEvent,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useState,
} from "react";

interface MultiSelectorProps
  extends React.ComponentPropsWithoutRef<typeof CommandPrimitive> {
  values: string[];
  onValuesChange: (value: string[]) => void;
  loop?: boolean;
  allowClearAll?: boolean;
  allowAddAll?: boolean;
  allOptions?: string[];
}

interface MultiSelectContextProps {
  value: string[];
  onValueChange: (value: string) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  ref: React.RefObject<HTMLInputElement>;
  handleSelect: (e: React.SyntheticEvent<HTMLInputElement>) => void;
  /** Clear every selected value at once */
  clearAll: () => void;
  allowClearAll: boolean;
  /** Add all available options at once */
  addAll: () => void;
  allowAddAll: boolean;
  allOptions: string[];
}

const MultiSelectContext = createContext<MultiSelectContextProps | null>(null);

const useMultiSelect = () => {
  const context = useContext(MultiSelectContext);
  if (!context) {
    throw new Error("useMultiSelect must be used within MultiSelectProvider");
  }
  return context;
};

/**
 * MultiSelect Docs: {@link: https://shadcn-extension.vercel.app/docs/multi-select}
 */

// TODO : expose the visibility of the popup

const MultiSelector = ({
  values: value,
  onValuesChange: onValueChange,
  loop = false,
  allowClearAll = true,
  allowAddAll = false,
  allOptions = [],
  className,
  children,
  dir,
  ...props
}: MultiSelectorProps) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isValueSelected, setIsValueSelected] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState("");

  const onValueChangeHandler = useCallback(
    (val: string) => {
      if (value.includes(val)) {
        onValueChange(value.filter((item) => item !== val));
      } else {
        onValueChange([...value, val]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  );

  const clearAll = useCallback(() => {
    if (value.length) {
      onValueChange([]);        // empty selection
      setOpen(false);           // close the dropdown
      inputRef.current?.blur(); // unfocus the input
    }
  }, [onValueChange, value.length, setOpen]);

  const addAll = useCallback(() => {
    if (allOptions.length > 0) {
      onValueChange([...allOptions]); // select all options
      setOpen(false);                 // close the dropdown
      inputRef.current?.blur();       // unfocus the input
    }
  }, [onValueChange, allOptions, setOpen]);

  const handleSelect = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      e.preventDefault();
      const target = e.currentTarget;
      const selection = target.value.substring(
        target.selectionStart ?? 0,
        target.selectionEnd ?? 0,
      );

      setSelectedValue(selection);
      setIsValueSelected(selection === inputValue);
    },
    [inputValue],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const target = inputRef.current;

      if (!target) return;

      const moveNext = () => {
        const nextIndex = activeIndex + 1;
        setActiveIndex(
          nextIndex > value.length - 1 ? (loop ? 0 : -1) : nextIndex,
        );
      };

      const movePrev = () => {
        const prevIndex = activeIndex - 1;
        setActiveIndex(prevIndex < 0 ? value.length - 1 : prevIndex);
      };

      const moveCurrent = () => {
        const newIndex =
          activeIndex - 1 <= 0
            ? value.length - 1 === 0
              ? -1
              : 0
            : activeIndex - 1;
        setActiveIndex(newIndex);
      };

      switch (e.key) {
        case "ArrowLeft":
          if (dir === "rtl") {
            if (value.length > 0 && (activeIndex !== -1 || loop)) {
              moveNext();
            }
          } else {
            if (value.length > 0 && target.selectionStart === 0) {
              movePrev();
            }
          }
          break;

        case "ArrowRight":
          if (dir === "rtl") {
            if (value.length > 0 && target.selectionStart === 0) {
              movePrev();
            }
          } else {
            if (value.length > 0 && (activeIndex !== -1 || loop)) {
              moveNext();
            }
          }
          break;

        case "Backspace":
        case "Delete":
          if (value.length > 0) {
            if (activeIndex !== -1 && activeIndex < value.length) {
              onValueChangeHandler(value[activeIndex]);
              moveCurrent();
            } else {
              if (target.selectionStart === 0) {
                if (selectedValue === inputValue || isValueSelected) {
                  onValueChangeHandler(value[value.length - 1]);
                }
              }
            }
          }
          break;

        case "Enter":
          setOpen(true);
          break;

        case "Escape":
          if (activeIndex !== -1) {
            setActiveIndex(-1);
          } else if (open) {
            setOpen(false);
          }
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, inputValue, activeIndex, loop],
  );

  return (
    <MultiSelectContext.Provider
      value={{
        value,
        onValueChange: onValueChangeHandler,
        open,
        setOpen,
        inputValue,
        setInputValue,
        activeIndex,
        setActiveIndex,
        ref: inputRef,
        handleSelect,
        clearAll,
        allowClearAll,
        addAll,
        allowAddAll,
        allOptions,
      }}
    >
      <Command
        onKeyDown={handleKeyDown}
        className={cn(
          "overflow-visible bg-transparent flex flex-col space-y-2",
          className,
        )}
        data-state={open ? "open" : "closed"}
        dir={dir}
        {...props}
      >
        {children}
      </Command>
    </MultiSelectContext.Provider>
  );
};

const MultiSelectorTrigger = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const {
    value,
    onValueChange,
    activeIndex,
    clearAll,
    allowClearAll,
    addAll,
    allowAddAll,
    allOptions,
    ref: inputRef,
  } = useMultiSelect();

  const mousePreventDefault = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleWrapperMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // ignore clicks on descendant buttons (Clear all, remove-badge, etc.)
      if ((e.target as HTMLElement).closest("button")) return;

      const el = inputRef.current;
      if (!el) return;

      if (document.activeElement === el) {
        // already focused → blur and prevent the click from re-focusing
        e.preventDefault();
        el.blur();
      } else {
        // not focused → focus (and prevent default so caret doesn't jump)
        e.preventDefault();
        el.focus();
      }
    },
    [inputRef],
  );

  return (
    <div
      ref={ref}
      onMouseDown={handleWrapperMouseDown}
      className={cn(
        "ring-1 ring-muted rounded-lg relative",
        {
          "ring-1 focus-within:ring-ring": activeIndex === -1,
        },
        className,
      )}
      {...props}
    >
      {/* Combined badges and input row */}
      <div className="flex flex-wrap items-center gap-1 gap-y-2 p-1 pt-2 px-2 pr-28">
        {value.map((item, index) => {
          const isLastItem = value.length === 1;
          const showRemoveButton = allowClearAll || !isLastItem;
          
          return (
            <Badge
              key={item}
              className={cn(
                "px-2 rounded-xl flex items-center gap-1 lg:px-1",
                activeIndex === index && "ring-2 ring-muted-foreground ",
              )}
              variant={"secondary"}
            >
              <span className="text-sm lg:text-xs">{item}</span>
              {showRemoveButton && (
                <button
                  aria-label={`Remove ${item} option`}
                  aria-roledescription="button to remove option"
                  type="button"
                  onMouseDown={mousePreventDefault}
                  onClick={() => onValueChange(item)}
                >
                  <span className="sr-only">Remove {item} option</span>
                  <RemoveIcon className="h-4 w-4 hover:stroke-destructive" />
                </button>
              )}
            </Badge>
          );
        })}
        
        {/* Input and action buttons */}
        <div className="flex flex-wrap items-center gap-1 min-w-0 w-full">
          {children}
        </div>
      </div>
      
      {/* Absolutely positioned action buttons */}
      <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1 w-28 justify-end">
        {allowAddAll && allOptions.length > 0 && value.length < allOptions.length && (
          <button
            type="button"
            onMouseDown={mousePreventDefault}
            onClick={addAll}
            className="flex-shrink-0 px-2 py-1 text-xs text-muted-foreground hover:text-primary whitespace-nowrap rounded hover:bg-muted/50 transition-colors"
          >
            Add all
          </button>
        )}
        {value.length > 0 && allowClearAll && (
          <button
            type="button"
            onMouseDown={mousePreventDefault}
            onClick={clearAll}
            className="flex-shrink-0 px-2 py-1 text-xs text-muted-foreground hover:text-destructive whitespace-nowrap rounded hover:bg-muted/50 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
});

MultiSelectorTrigger.displayName = "MultiSelectorTrigger";

const MultiSelectorInput = forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => {
  const {
    setOpen,
    inputValue,
    setInputValue,
    activeIndex,
    setActiveIndex,
    handleSelect,
    ref: inputRef,
  } = useMultiSelect();

  const handleBlur = (e: React.FocusEvent) => {
    // Don't close if the focus is moving to an element that should keep the dropdown open
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget) {
      // Check if the new focus target is a filter select or other control that should keep dropdown open
      const isFilterElement = relatedTarget.closest('[data-keep-multiselect-open]') || 
                             relatedTarget.closest('[role="combobox"]') ||
                             relatedTarget.closest('[role="listbox"]') ||
                             relatedTarget.closest('button[role="combobox"]');
      
      if (isFilterElement) {
        return; // Don't close the dropdown
      }
    }
    
    // Small delay to allow for clicks on dropdown items
    setTimeout(() => setOpen(false), 150);
  };

  return (
    <CommandPrimitive.Input
      {...props}
      tabIndex={0}
      ref={inputRef}
      value={inputValue}
      onValueChange={activeIndex === -1 ? setInputValue : undefined}
      onSelect={handleSelect}
      onBlur={handleBlur}
      onFocus={() => setOpen(true)}
      onClick={() => setActiveIndex(-1)}
      className={cn(
        "ml-2 bg-transparent outline-none placeholder:text-muted-foreground flex-auto min-w-[20px] basis-[20px] max-w-full",
        className,
        activeIndex !== -1 && "caret-transparent",
      )}
    />
  );
});

MultiSelectorInput.displayName = "MultiSelectorInput";

const MultiSelectorContent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children }, ref) => {
  const { open, ref: inputRef } = useMultiSelect();
  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    input.addEventListener('focus', handleFocus);
    input.addEventListener('blur', handleBlur);

    return () => {
      input.removeEventListener('focus', handleFocus);
      input.removeEventListener('blur', handleBlur);
    };
  }, [inputRef]);

  return (
    <div ref={ref} className="relative">
      <AnimatePresence mode="wait">
        {open && isFocused && (
          <motion.div
            key="multiselect-content"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ 
              duration: 0.15, 
              ease: "easeOut",
              scale: { duration: 0.12 }
            }}
            className="absolute w-full z-[100] mt-1"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

MultiSelectorContent.displayName = "MultiSelectorContent";

const MultiSelectorList = forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, children }, ref) => {
  return (
    <CommandList
      ref={ref}
      className={cn(
        "p-2 flex flex-col gap-2 rounded-md scrollbar-thin scrollbar-track-transparent transition-colors scrollbar-thumb-muted-foreground dark:scrollbar-thumb-muted scrollbar-thumb-rounded-lg w-full bg-background shadow-md border border-muted max-h-60 overflow-y-auto",
        className,
      )}
    >
      {children}
      <CommandEmpty>
        <span className="text-muted-foreground">No results found</span>
      </CommandEmpty>
    </CommandList>
  );
});

MultiSelectorList.displayName = "MultiSelectorList";

const MultiSelectorItem = forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  { value: string } & React.ComponentPropsWithoutRef<
    typeof CommandPrimitive.Item
  >
>(({ className, value, children, ...props }, ref) => {
  const { value: Options, onValueChange, setInputValue } = useMultiSelect();

  const mousePreventDefault = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleSelect = useCallback(() => {
    onValueChange(value);
    setInputValue("");
    
    // Focus the next available item after a short delay
    setTimeout(() => {
      const currentItem = document.querySelector(`[data-value="${CSS.escape(value)}"]`);
      if (currentItem) {
        // Find the next sibling that's not selected
        let nextItem = currentItem.nextElementSibling;
        while (nextItem && nextItem.getAttribute('aria-disabled') === 'true') {
          nextItem = nextItem.nextElementSibling;
        }
        
        // If no next item, try the previous sibling
        if (!nextItem) {
          let prevItem = currentItem.previousElementSibling;
          while (prevItem && prevItem.getAttribute('aria-disabled') === 'true') {
            prevItem = prevItem.previousElementSibling;
          }
          nextItem = prevItem;
        }
        
        // Focus the found item
        if (nextItem && nextItem instanceof HTMLElement) {
          nextItem.focus();
        }
      }
    }, 50);
  }, [value, onValueChange, setInputValue]);

  const isIncluded = Options.includes(value);
  return (
    <CommandItem
      ref={ref}
      {...props}
      data-value={value}
      onSelect={handleSelect}
      className={cn(
        "rounded-md cursor-pointer px-2 py-1 transition-colors flex justify-between ",
        className,
        isIncluded && "opacity-50 cursor-default",
        props.disabled && "opacity-50 cursor-not-allowed",
      )}
      onMouseDown={mousePreventDefault}
    >
      {children}
      {isIncluded && <Check className="h-4 w-4" />}
    </CommandItem>
  );
});

MultiSelectorItem.displayName = "MultiSelectorItem";

export {
  MultiSelector,
  MultiSelectorTrigger,
  MultiSelectorInput,
  MultiSelectorContent,
  MultiSelectorList,
  MultiSelectorItem,
};
